/**
 * Auth store - decodes the JWT from localStorage to expose user context.
 *
 * No persistence needed: derived from the already-persisted token.
 */

import { create } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import { getAuthToken } from '@/api/auth';

interface JwtPayload {
  sub: string;
  firm_id: string;
  user_name: string;
  firm_name: string;
  email: string;
  permissions: string | string[];
  exp: number;
}

export interface AuthUser {
  userId: string;
  firmId: number;
  userName: string;
  firmName: string;
  email: string;
  permissions: string[];
  isSuperAdmin: boolean;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  hydrate: () => void;
  clear: () => void;
}

/**
 * Normalize a JWT `permissions` claim to a string[]. Accepts a JSON-encoded
 * string (dev token), a real array claim (Shell), or a delimited string;
 * anything else yields []. Never throws.
 */
export function normalizePermissions(raw: unknown): string[] {
  let value: unknown = raw;
  if (typeof value === 'string') {
    const rawString = value;
    try {
      value = JSON.parse(rawString);
    } catch {
      value = rawString.split(/[,\s]+/).filter(Boolean);
    }
  }
  return Array.isArray(value) ? value.filter((p): p is string => typeof p === 'string') : [];
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,

  hydrate: () => {
    const token = getAuthToken();
    if (!token) {
      set({ user: null, isAuthenticated: false });
      return;
    }

    try {
      const payload = jwtDecode<JwtPayload>(token);

      // Check expiration
      if (payload.exp && payload.exp * 1000 < Date.now()) {
        set({ user: null, isAuthenticated: false });
        return;
      }

      const firmId = parseInt(payload.firm_id, 10);
      const permissions = normalizePermissions(payload.permissions);

      const user: AuthUser = {
        userId: payload.sub,
        firmId: isNaN(firmId) ? 0 : firmId,
        userName: payload.user_name,
        firmName: payload.firm_name,
        email: payload.email,
        permissions,
        isSuperAdmin: firmId === 0,
      };

      set({ user, isAuthenticated: true });
    } catch {
      // Malformed token - clear state
      set({ user: null, isAuthenticated: false });
    }
  },

  clear: () => {
    set({ user: null, isAuthenticated: false });
  },
}));
